import VideoPlayer, { VideoPlayerOptions } from '@anm/components/VideoPlayer';
import Button, { ButtonProps, ButtonSize, ButtonVariants } from '@anm/components/buttons/Button';
import LazyImage from '@anm/components/image/LazyImage';
import CardModal, { CardModalProps } from '@anm/components/modals/CardModal';
import { EMBED_INTERNAL_PARAM } from '@anm/constants/wave-editor';
import useHLSVideo from '@anm/hooks/useHLSVideo';
import usePageParams from '@anm/hooks/usePageParams';
import { useRef, FC } from 'react';

import Wrapper, { ButtonContainer, MediaContainer, StockPrice } from './Wrapper';
import { useAnimation } from './hooks';

export * from './Wrapper';

type StockModalProps = CardModalProps & {
  title: string;
  isVideo: boolean;
  thumbnail_url: string;
  priceValue?: string;
  preview_url?: string;
  playerOptions?: VideoPlayerOptions;
  templateFromStockUrl?: string;
  handleAddStock?(): void;
  renderAnonButton?(props: Omit<ButtonProps, 'onClick'> & { onClick?(): void }): JSX.Element;
};

const StockModal: FC<StockModalProps> = ({
  title,
  isVideo,
  priceValue,
  preview_url = '',
  cardSettings,
  thumbnail_url,
  playerOptions,
  templateFromStockUrl,
  onModalClose,
  handleAddStock,
  renderAnonButton
}) => {
  const { embed } = usePageParams();
  const isAnimating = useAnimation();
  const isInternalEmbed = embed === EMBED_INTERNAL_PARAM;

  const buttonText = isInternalEmbed ? 'Add' : 'Make Video';

  const buttonProps = {
    size: 'large' as ButtonSize,
    variant: 'light-blue-lowercase' as ButtonVariants,
    children: buttonText,
    ...(isInternalEmbed ? { onClick: handleAddStock } : { href: templateFromStockUrl })
  };

  const videoRef = useRef<HTMLVideoElement | null>(null);
  useHLSVideo({ videoRef, hlsVideo: preview_url });

  return (
    <CardModal {...{ cardSettings, onModalClose }}>
      <Wrapper {...{ isAnimating }}>
        <MediaContainer>
          {priceValue && <StockPrice>{priceValue}</StockPrice>}
          {isVideo ? (
            <VideoPlayer videoRef={videoRef} src={preview_url!} poster={thumbnail_url} options={playerOptions} />
          ) : (
            <LazyImage src={preview_url || thumbnail_url} alt={title} />
          )}
        </MediaContainer>
        {templateFromStockUrl && (
          <ButtonContainer>
            {renderAnonButton ? renderAnonButton(buttonProps) : <Button {...buttonProps} />}
          </ButtonContainer>
        )}
      </Wrapper>
    </CardModal>
  );
};

export default StockModal;
